import { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { Week52HighLowResponse, Week52HighLowStatusType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getWeek52HighLow as getWeek52HighLowCall } from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isCallStatusPending, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../lib/util/typeGuards';

import { usePriceAndChartLoading, useSubPageTitle } from '../../hooks';
import { useSubscriptionManager } from '../../hooks/useSubscriptionManager';
import { PopularGroupItemRow } from '../../shared_components/PopularGroups/PopularGroups.types';
import Week52GainersAndLosersInternal from '../../shared_components/Week52GainersAndLosers/Week52GainersAndLosersInternal/Week52GainersAndLosersInternal';
import { UI_ROUTES } from '../../util/routes';

const WEEK_52_HIGH_LOW_TAB_STATE_KEY = 'week52HighLowTab';
const GAINERS_TAB_INDEX = 0;
const LOSERS_TAB_INDEX = 1;
const PAGE_SIZE = 10;

const Week52GainersAndLosersInternalWrapper: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const {
    getWeek52HighLow,
    isWeek52HighLowReady,
    isWeek52HighLowPending,
    week52High,
    week52Low,
    companyLogoAndName,
  } = props;

  const location = useLocation();
  const titleRef = useSubPageTitle<HTMLDivElement>();
  const navigate = useNavigate();
  const { state } = location;

  const [activeTab, setActiveTab] = useState<number>(
    state?.[WEEK_52_HIGH_LOW_TAB_STATE_KEY]
      ?? (state?.isTopGainersSelected === false ? LOSERS_TAB_INDEX : GAINERS_TAB_INDEX),
  );
  const [changeStatus, setChangeStatus] = useState<Week52HighLowStatusType | null>(null);
  const [content, setContent] = useState<Week52HighLowResponse[]>([]);
  const [page, setPage] = useState<number>(0);

  const isGainersTab = changeStatus === Week52HighLowStatusType.Gainers;
  const isReadyAndNoData = isWeek52HighLowReady
    && (isGainersTab ? isEmpty(week52High.content) : isEmpty(week52Low.content));

  // Single market: the price cache is keyed by the plain symbol and prices are in dollars.
  const rows = useMemo((): PopularGroupItemRow[] => content.map((item: Week52HighLowResponse) => ({
    item,
    chartSymbol: item.symbol,
    countryDesignation: '',
    currencySymbol: CurrencySymbolEnum.USD,
    isEuropeanSymbol: false,
    companyName: item.name ?? companyLogoAndName[item.symbol]?.name ?? '',
  })), [content, companyLogoAndName]);

  const symbolsToSubscribe = useMemo(() => {
    const week52Data = (isGainersTab ? week52High.content : week52Low.content) || [];

    return week52Data.map(item => ({
      symbol: item.symbol,
      stockExchangeCode: item.stockExchangeCode,
      countryDesignation: companyLogoAndName[item.symbol]?.countryDesignation,
    }));
  }, [isGainersTab, week52High.content, week52Low.content, companyLogoAndName]);

  useSubscriptionManager({
    symbols: symbolsToSubscribe,
    enabled: isWeek52HighLowReady && symbolsToSubscribe.length > 0,
  });

  useEffect(() => {
    setChangeStatus(activeTab === GAINERS_TAB_INDEX
      ? Week52HighLowStatusType.Gainers
      : Week52HighLowStatusType.Losers);
  }, [activeTab]);

  useEffect(() => {
    if (!changeStatus) return;

    const currentTabContent = isGainersTab ? week52High.content : week52Low.content;

    if (currentTabContent.length === 0 && !isWeek52HighLowPending) {
      getWeek52HighLow({ changeStatus });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeStatus]);

  useEffect(() => {
    if (!changeStatus) return;
    setContent([]);
    setContent(isGainersTab ? week52High.content : week52Low.content);
  }, [changeStatus, week52High.content, week52Low.content]);

  const currentContent = isGainersTab ? week52High.content : week52Low.content;

  const isLoading = usePriceAndChartLoading({
    stocks: content,
    isApiPending: isWeek52HighLowPending && currentContent.length === 0,
  });

  const handleLoadMore = () => {
    const nextPage = Math.ceil(content.length / PAGE_SIZE);
    setPage(nextPage);
    getWeek52HighLow({ page: nextPage, changeStatus });
  };

  const handleSymbolClick = (symbol: string) => {
    navigate(`${UI_ROUTES.symbol}/${symbol}`);
  };

  const handleGoBack = () => navigate(UI_ROUTES.discover);

  const totalPages = isGainersTab ? week52High.totalPages : week52Low.totalPages;

  return (
    <Week52GainersAndLosersInternal
      rows={rows}
      activeTabIndex={activeTab}
      isReady={isWeek52HighLowReady}
      isLoading={isLoading}
      isNoData={isReadyAndNoData}
      hasMorePages={page + 1 < totalPages}
      pageSize={PAGE_SIZE}
      onTabChange={setActiveTab}
      onLoadMore={handleLoadMore}
      onSymbolClick={handleSymbolClick}
      isAce
      titleRef={titleRef}
      onGoBack={handleGoBack}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isWeek52HighLowReady: isCallStatusReady(FundamentalCall.getWeek52HighLow, state.fundamental.statusByCall),
  isWeek52HighLowPending: isCallStatusPending(FundamentalCall.getWeek52HighLow, state.fundamental.statusByCall),
  week52High: state.fundamental.week52High,
  week52Low: state.fundamental.week52Low,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getWeek52HighLow: (params) => dispatch(getWeek52HighLowCall(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Week52GainersAndLosersInternalWrapper);
